import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Languages, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { LANGUAGES } from '../i18n';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Language picker: switches the UI at once and saves the choice to the user's profile. */
export default function LanguageSwitcher({ className = '' }) {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const current = LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) || LANGUAGES[0];

  const choose = async (code) => {
    if (code === i18n.resolvedLanguage) return;
    await i18n.changeLanguage(code);
    if (user) {
      try {
        await axios.put(`${API_URL}/api/auth/profile`, { language: code }, { withCredentials: true });
      } catch {
        toast.error(t('common:language.saveFailed'));
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="language-switcher" aria-label={t('common:language.label')}
          className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 ${className}`}>
          <Languages className="h-4 w-4" /> {current.short}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem key={l.code} onSelect={() => choose(l.code)} data-testid={`language-${l.code}`}>
            <span className="w-8 text-xs text-slate-500">{l.short}</span>
            <span className="flex-1">{l.label}</span>
            {l.code === current.code && <Check className="h-4 w-4 ml-2" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
